import * as THREE from "three";

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useAnimations, useGLTF } from "@react-three/drei";
import { useRoute } from "wouter";

import { SkeletonUtils } from "three-stdlib";

import { scaleAnimationDelta } from "../../utils/animationTiming";
import { assetUrl } from "../../utils/paths";
import {
  resolvePortalSpatialView,
  WORLDX_SECTORS,
  worldxSectorFromCenterDirection,
} from "../../world-model/portalSpatialModel";
import {
  clearWorldxCenterEvidence,
  publishWorldxCenterEvidence,
} from "../../world-model/worldxCenterEvidence";

export function FadableModel2({
  stabilizePortalAlphaMaterial,
  position = [0, -1, -2],
  scale = [0.5, 0.5, 0.5],
  isVisible = false,
  duration = 10,
  outDuration = null,
  inDelay = 0,
  outDelay = 0,
  forcedOpacity = null,
  portalId,
  speedFactor = 0.5,
  targetOpacity = 1,
  currentMesh = null,
  currentStage = null,
}) {
  const group = useRef();
  const camera = useThree((state) => state.camera);
  const { scene: src, animations } = useGLTF(assetUrl("/worldx.glb"));
  const scene = useMemo(() => (src ? SkeletonUtils.clone(src) : null), [src]);
  const { actions, mixer } = useAnimations(animations, group);

  const [, params] = useRoute("/neo/:id");
  const isActive = params?.id === portalId;

  useEffect(() => {
    if (!actions) return;
    Object.values(actions).forEach((a) => {
      if (!a) return;
      a.clampWhenFinished = false;
      a.setLoop(THREE.LoopRepeat, Infinity);
      a.play();
      a.paused = !isActive;
    });
    return () => mixer?.stopAllAction();
  }, [actions, isActive, mixer]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.__worldxAnimationControl = (command = "play") => {
      const animationActions = Object.values(actions || {}).filter(Boolean);
      if (!animationActions.length) return false;
      for (const action of animationActions) {
        if (command === "restart") {
          action.reset();
          action.setLoop(THREE.LoopRepeat, Infinity);
          action.play();
          action.paused = false;
        } else if (command === "pause") {
          action.paused = true;
        } else {
          action.play();
          action.paused = false;
        }
      }
      mixer?.update(0);
      return true;
    };
    return () => {
      delete window.__worldxAnimationState;
      delete window.__worldxAnimationControl;
    };
  }, [actions, mixer]);

  useEffect(() => () => clearWorldxCenterEvidence(), []);

  const matsRef = useRef([]);
  const progressRef = useRef(0);
  const opacityRef = useRef(0);
  const targetRef = useRef(0);
  const pendingRef = useRef(false);
  const delayLeftRef = useRef(0);
  const lastVisibleRef = useRef(isVisible);
  const cameraCenterDirectionRef = useRef(new THREE.Vector3());
  const cameraLocalPositionRef = useRef(new THREE.Vector3());
  const worldxLocalDirectionRef = useRef(new THREE.Vector3());
  const worldxQuaternionRef = useRef(new THREE.Quaternion());
  const projectedLandmarkRef = useRef(new THREE.Vector3());
  const lastCenterSectorRef = useRef(null);

  useLayoutEffect(() => {
    if (!scene) return;
    scene.userData = {
      ...scene.userData,
      entityId: "meet_joz_semantic_city",
      objectId: "meet_joz_semantic_city",
      portalId: "meet-joz",
      semanticRole: "360 semantic environment",
      semanticMeaning:
        "Worldx is the abstract gold semantic city of proof, aura, and capability surrounding the Meet Joz sequence.",
      semanticChildren: [
        {
          id: "meet_joz_heart",
          role: "semantic landmark",
          meaning: "Central emotional and prestige anchor of Ascend.",
        },
        {
          id: "meet_joz_scale_maxx",
          role: "semantic landmark",
          meaning: "Scale, reach, and magnitude.",
        },
        {
          id: "meet_joz_clout_maxx",
          role: "semantic landmark",
          meaning: "Recognition, influence, and signal.",
        },
        {
          id: "meet_joz_world_class",
          role: "semantic landmark",
          meaning: "International proof, Singapore framing, and global scale.",
        },
        {
          id: "meet_joz_alpha_psl",
          role: "semantic landmark",
          meaning: "Dubai prestige and regional proof.",
        },
        {
          id: "meet_joz_atmos_maxx",
          role: "semantic landmark",
          meaning: "Atmosphere as a deliberate design variable.",
        },
        {
          id: "meet_joz_cross_sensory_aura_engineering",
          role: "semantic landmark",
          meaning: "Intentional construction of aura through sensory composition.",
        },
        {
          id: "meet_joz_maximize_beauty_change_reality",
          role: "semantic landmark",
          meaning: "Beauty as an operational force that changes energy, experience, and perceived reality.",
        },
        {
          id: "meet_joz_ai_synthesis",
          role: "semantic landmark",
          meaning: "Connection-making, integration, and generative synthesis.",
        },
        {
          id: "meet_joz_ai_analysis",
          role: "semantic landmark",
          meaning: "Structured analysis and intelligence.",
        },
        {
          id: "meet_joz_capsule",
          role: "Gold Pill inside Ascend",
          meaning:
            "The Ascend capsule is the Gold Pill: combined skill, judgment, creativity, technical ability, design, engineering, and execution used to create high-quality innovation with AI.",
        },
      ],
      interactive: false,
    };
    matsRef.current = [];

    scene.traverse((o) => {
      if (!o.isMesh || !o.material) return;
      if (o.name === "qq.002") return;

      const setUp = (mat) => {
        const next = mat.clone();
        next.transparent = true;
        next.opacity = 0;
        next.alphaTest = 0;
        next.depthWrite = false;
        next.premultipliedAlpha = false;
        stabilizePortalAlphaMaterial(next);
        return next;
      };

      if (Array.isArray(o.material)) {
        o.material = o.material.map(setUp);
        matsRef.current.push(...o.material);
      } else {
        o.material = setUp(o.material);
        matsRef.current.push(o.material);
      }

      o.renderOrder = 10;
      o.onBeforeRender = () => {
        if (Array.isArray(o.material)) {
          o.material.forEach((m) => {
            m.opacity = opacityRef.current;
          });
        } else {
          o.material.opacity = opacityRef.current;
        }
      };
    });

    progressRef.current = 0;
    opacityRef.current = 0;
    targetRef.current = isVisible ? 1 : 0;
  }, [isVisible, scene, stabilizePortalAlphaMaterial]);

  useLayoutEffect(() => {
    if (!scene) return;

    scene.traverse((o) => {
      if (!o.isMesh) return;
      if (o.name.includes("qq")) {
        const glassMat = new THREE.MeshPhysicalMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0,
          depthWrite: false,
          roughness: 0.05,
          metalness: 0,
          transmission: 1.0,
          ior: 1.5,
          thickness: 0.5,
          clearcoat: 1.0,
          clearcoatRoughness: 0,
        });

        o.material = glassMat;
        matsRef.current.push(glassMat);

        o.renderOrder = 10;
        o.onBeforeRender = () => {
          o.material.opacity = opacityRef.current;
        };
      }
    });

    progressRef.current = 0;
    opacityRef.current = 0;
    targetRef.current = isVisible ? 1 : 0;
  }, [isVisible, scene]);

  useEffect(() => {
    if (forcedOpacity !== null) {
      targetRef.current = forcedOpacity;
    }
  }, [forcedOpacity]);

  useEffect(() => {
    if (isVisible && inDelay > 0) {
      pendingRef.current = true;
      delayLeftRef.current = inDelay;
    }
  }, [inDelay, isVisible]);

  useEffect(() => {
    targetRef.current = isVisible ? 1 : 0;
    if (isVisible !== lastVisibleRef.current) {
      const delay = isVisible ? inDelay : outDelay;
      if (delay > 0) {
        pendingRef.current = true;
        delayLeftRef.current = delay;
      }
      lastVisibleRef.current = isVisible;
    }
  }, [inDelay, isVisible, outDelay]);

  useFrame((_, dt) => {
    const animationDelta = scaleAnimationDelta(dt);
    if (mixer && isActive) {
      mixer.update(animationDelta * speedFactor);
    }

    if (typeof window !== "undefined") {
      window.__worldxAnimationState = {
        observedAt: new Date().toISOString(),
        active: isActive,
        visible: isVisible,
        speedFactor,
        opacity: opacityRef.current,
        opacityProgress: progressRef.current,
        targetOpacity,
        forcedOpacity,
        clips: Object.entries(actions || {}).map(([name, action]) => ({
          name,
          time: Number((action?.time || 0).toFixed(4)),
          duration: Number((action?.getClip?.()?.duration || 0).toFixed(4)),
          paused: Boolean(action?.paused),
          enabled: Boolean(action?.enabled),
          effectiveWeight: Number((action?.getEffectiveWeight?.() || 0).toFixed(4)),
          effectiveTimeScale: Number((action?.getEffectiveTimeScale?.() || 0).toFixed(4)),
          loopCount: action?._loopCount ?? null,
        })),
      };

      if (isActive && isVisible && group.current) {
        camera.getWorldDirection(cameraCenterDirectionRef.current);
        group.current.getWorldQuaternion(worldxQuaternionRef.current);
        worldxQuaternionRef.current.invert();
        worldxLocalDirectionRef.current
          .copy(cameraCenterDirectionRef.current)
          .applyQuaternion(worldxQuaternionRef.current)
          .normalize();
        const directionSector = worldxSectorFromCenterDirection(worldxLocalDirectionRef.current);
        camera.getWorldPosition(cameraLocalPositionRef.current);
        group.current.worldToLocal(cameraLocalPositionRef.current);

        let projectedSectorIndex = null;
        let projectedCenterDistance = Infinity;
        WORLDX_SECTORS.forEach((sector, index) => {
          const radians = THREE.MathUtils.degToRad(sector.captureHeadingDegrees);
          const elevationRadians = THREE.MathUtils.degToRad(sector.captureElevationDegrees || 0);
          const calibrationRadius = 8;
          projectedLandmarkRef.current.set(
            Math.sin(radians) * calibrationRadius,
            cameraLocalPositionRef.current.y + Math.tan(elevationRadians) * calibrationRadius,
            -Math.cos(radians) * calibrationRadius
          );
          group.current.localToWorld(projectedLandmarkRef.current);
          projectedLandmarkRef.current.project(camera);
          if (projectedLandmarkRef.current.z < -1 || projectedLandmarkRef.current.z > 1) return;
          const centerDistance = Math.hypot(
            projectedLandmarkRef.current.x,
            projectedLandmarkRef.current.y
          );
          if (centerDistance < projectedCenterDistance) {
            projectedCenterDistance = centerDistance;
            projectedSectorIndex = index;
          }
        });
        const projectedSector = Number.isInteger(projectedSectorIndex)
          ? WORLDX_SECTORS[projectedSectorIndex]
          : null;
        const centerSector = projectedSector && directionSector
          ? {
              ...directionSector,
              sectorIndex: projectedSectorIndex,
              label: projectedSector.label,
              meaning: projectedSector.meaning,
              calibratedHeadingDegrees: projectedSector.captureHeadingDegrees,
              calibratedElevationDegrees: projectedSector.captureElevationDegrees || 0,
              viewportCenterDistance: Number(projectedCenterDistance.toFixed(4)),
              selectionMethod: "projected-2d-landmark-center",
            }
          : directionSector;

        const centerSignature = centerSector
          ? `${currentMesh || ""}:${currentStage || ""}:${centerSector.sectorIndex}`
          : null;
        if (centerSector && centerSignature !== lastCenterSectorRef.current) {
          lastCenterSectorRef.current = centerSignature;
          const pitchDegrees = Number((Math.asin(THREE.MathUtils.clamp(
            worldxLocalDirectionRef.current.y,
            -1,
            1
          )) * 180 / Math.PI).toFixed(1));
          const lookAtEvidence = resolvePortalSpatialView({
            portal: "meet-joz",
            stage: currentStage,
            mesh: currentMesh,
            yawDegrees: centerSector.headingDegrees,
            pitchDegrees,
          });

          const centerEvidence = publishWorldxCenterEvidence({
            method: centerSector.selectionMethod || "camera-center-model-frame",
            headingDegrees: centerSector.headingDegrees,
            pitchDegrees,
            sectorIndex: centerSector.sectorIndex,
            label: centerSector.label,
            meaning: centerSector.meaning,
            calibratedHeadingDegrees: centerSector.calibratedHeadingDegrees ?? null,
            calibratedElevationDegrees: centerSector.calibratedElevationDegrees ?? null,
            viewportCenterDistance: centerSector.viewportCenterDistance ?? null,
            modelAsset: "worldx.glb",
            canvasCoordinates: { x: 0, y: 0 },
          });
          window.dispatchEvent(new CustomEvent("world-camera-telemetry", {
            detail: {
              portal: "meet-joz",
              mesh: currentMesh,
              stage: currentStage,
              worldxVisible: true,
              worldxSector: centerSector.sectorIndex,
              centerEvidence,
              cameraState: {
                view: {
                  yaw_degrees: centerSector.headingDegrees,
                  pitch_degrees: pitchDegrees,
                },
              },
              lookAtEvidence,
            },
          }));
        }
      } else {
        lastCenterSectorRef.current = null;
        clearWorldxCenterEvidence();
      }
    }

    if (pendingRef.current) {
      delayLeftRef.current -= animationDelta;
      if (delayLeftRef.current > 0) return;
      pendingRef.current = false;
    }

    const target = forcedOpacity !== null ? forcedOpacity : targetRef.current;
    let progress = progressRef.current;
    if (Math.abs(progress - target) < 1e-4) return;

    const dir = target > progress ? 1 : -1;
    const activeDuration = dir > 0 ? duration : outDuration ?? duration;
    progress += (animationDelta / activeDuration) * dir;
    progress = THREE.MathUtils.clamp(progress, 0, 1);

    const eased = progress * progress * (3 - 2 * progress);
    const visibleOpacity = forcedOpacity !== null
      ? 1
      : THREE.MathUtils.clamp(Number(targetOpacity) || 0, 0, 1);
    const renderedOpacity = eased * visibleOpacity;
    matsRef.current.forEach((m) => {
      m.opacity = renderedOpacity;
    });
    opacityRef.current = renderedOpacity;
    progressRef.current = progress;
  });

  const handleClick = () => {
    if (!actions) return;
    Object.values(actions).forEach((a) => {
      if (!a) return;
      a.paused = !a.paused;
    });
  };

  if (!scene) return null;
  return (
    <group ref={group} position={position} scale={scale} onClick={handleClick}>
      <primitive object={scene} />
    </group>
  );
}
